import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { TCreateDealPayload, TDeal } from "@/models/deals.model";
import { useCouponsStore } from "@/store/coupons/coupons.store";
import { useProductsStore } from "@/store/products/products.store";
import { usePipelinesStore } from "@/store/pipelines/pipelines.store";
import { useDealsStore } from "@/store/deals/deals.store";
import { getErrorFromBackend } from "@/utils/errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectGroup, SelectValue } from "@radix-ui/react-select";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type Props = {
  open: boolean;
  leadId?: number;
  onClose: () => void;
  onDealCreated: (deal: TDeal) => void;
};

export const createDealSchema = z.object({
   pipeline: z.number({ required_error: "Pipeline is required" }),
  name: z.string({ required_error: "Deal name is required" }).min(1, "Deal name is required"), 
   status: z.number({ required_error: "Status is required" }),  
  amount: z
  .number()
  .optional()
  .refine((val) => val !== undefined && val > 0, {
    message: "Amount is required",
  }),
  currency: z.string().min(1, "Currency is required"),
  tags: z.array(z.string()).optional(),
  leadId: z.number({ required_error: "Lead is required" }),
});

const AddDealSidebar = ({ open, onClose, leadId, onDealCreated }: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const useFormReturn = useForm<TCreateDealPayload>({
    resolver: zodResolver(createDealSchema),
    defaultValues: { leadId, currency: "USD" },
  });
  const { handleSubmit, setValue, clearErrors, control, formState, watch } = useFormReturn;
  const { isSubmitting } = formState;

  // Get pipelines from store
  const pipelines = Object.values(usePipelinesStore((s) => s.entities));

  // Watch selected pipeline to filter statuses
  const selectedPipelineId = watch("pipeline");

  // Get statuses for selected pipeline
  const availableStatuses = pipelines
    .find(p => p.id === selectedPipelineId)
    ?.status || [];

  const onSubmit = handleSubmit(async (data) => {
    try {
      const createdDeal = await useDealsStore.getState().createDeal(data);
      onDealCreated(createdDeal);
      enqueueSnackbar("Deal added successfully", { variant: "success" });
      onClose();
    } catch (e) {
      enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
    }
  });

  useEffect(() => {
    usePipelinesStore.getState().getAllPipelines();
    useProductsStore.getState().getAllProducts();
    useCouponsStore.getState().getAllCoupons();
  }, []);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Deal</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Form {...useFormReturn}>
            <form onSubmit={onSubmit} className="space-y-8">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deal Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter deal name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="pipeline"
                render={() => (
                  <FormItem>
                    <FormLabel>Pipeline</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(v) => {
                          setValue("pipeline", Number(v), { shouldDirty: true });
                          clearErrors("pipeline");
                          // Reset status when pipeline changes
                          setValue("status", 0);
                          clearErrors("status");
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose pipeline" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {pipelines.map((pipeline) => (
                              <SelectItem key={pipeline.id} value={`${pipeline.id}`}>
                                {pipeline.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="status"
                render={() => (
                  <FormItem>
                    <FormLabel>Deal Status</FormLabel>
                    <FormControl>
                      <Select
                        disabled={!selectedPipelineId}
                        onValueChange={(v) => {
                          setValue("status", Number(v), { shouldDirty: true });
                          clearErrors("status");
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {availableStatuses.map((status) => (
                              <SelectItem key={status.id} value={`${status.id}`}>
                                {status.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter amount"
                        type="number"
                        value={field.value || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === "" ? undefined : Number(value));
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Adding deal" : "Add Deal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddDealSidebar;